import React from 'react';
import { useChangeOrderSummary } from '../../hooks/useChangeOrders';
import { formatProjectDateOnly } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

function StatPill({ label, value }: { label: string; value: any }) {
    return (
        <div className="rounded-xl border bg-white px-3 py-2">
            <div className="text-[11px] text-gray-500">{label}</div>
            <div className="text-sm font-semibold tabular-nums">{value ?? '—'}</div>
        </div>
    );
}

export default function ChangeOrderSummaryBar({
    projectId,
    contractId,
    onCreate,
    canEdit,
}: {
    projectId: string;
    contractId: string;
    onCreate: () => void;
    canEdit?: boolean;
}) {
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';
    const q = useChangeOrderSummary(projectId, contractId, true);
    const s = q.data;

    return (
        <div className="rounded-2xl border bg-white overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
                <div className="text-sm font-medium">Change Orders</div>
                <button
                    type="button"
                    className="text-xs rounded-lg border px-2.5 py-1.5 bg-white hover:bg-gray-50 disabled:opacity-50"
                    onClick={onCreate}
                    disabled={!canEdit}
                    title={!canEdit ? '권한이 없습니다.' : undefined}
                >
                    + New CO
                </button>
            </div>

            <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                <StatPill label="Total" value={s?.total} />
                <StatPill label="Overdue" value={s?.overdueCount} />
                <StatPill label="Approved ΔCost" value={s ? s.sumCostDelta.approved : '—'} />
                <StatPill label="Open ΔCost" value={s ? s.sumCostDelta.open : '—'} />
            </div>

            <div className="px-4 pb-4">
                <div className="text-xs text-gray-500 mb-2">Top Overdue</div>
                {q.isLoading ? (
                    <div className="text-sm text-gray-500">Loading…</div>
                ) : (s?.topOverdue ?? []).length === 0 ? (
                    <div className="text-sm text-gray-500">Overdue 항목이 없습니다.</div>
                ) : (
                    <ul className="space-y-2">
                        {s!.topOverdue.map((r) => (
                            <li key={r.id} className="rounded-xl border bg-gray-50 px-3 py-2">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium truncate">{r.title}</div>
                                        <div className="text-[11px] text-gray-600">
                                            {r.status} · {r.overdueDays}d overdue · due{' '}
                                            {formatProjectDateOnly(r.nextDueDate, zone, '—')}
                                        </div>
                                    </div>
                                    <div className="text-[11px] text-gray-700 tabular-nums">
                                        Δ {r.costDelta ?? '—'} / {r.scheduleDeltaDays ?? '—'}d
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
